/**
 * Block 13 PR-G Phase 4B — Generic anketa renderer для Customer/Executor sides.
 *
 * Renders array of `ContractRequisite[]`:
 *   - Fixed rows (removable=false): label nepluha (static `<label>`), input для value.
 *   - Custom rows (removable=true): label input + value input + trash button.
 *   - "+ Добавить строку" dashed button внизу.
 *
 * **A11y:**
 *   - Each field has `htmlFor` association.
 *   - Trash button has `aria-label` referencing field label.
 *   - Add-button has `aria-label="Добавить строку"` + side context.
 *
 * **Tap targets (P-044):** `min-h-tap` (44px) на inputs + trash + add button.
 *
 * Reference:
 *   - prototype `screen-2.13.2-contract.html` `renderReqAnketa` function (lines 286-320).
 *   - Block 12 SectionConditions custom row pattern.
 */

import { useCallback, useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { ContractRequisite } from '@pomoshnik/shared';

const INPUT_CLASS =
  'w-full min-h-tap px-3 rounded-xl border-[1.5px] border-accent bg-card text-base text-ink ' +
  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none placeholder:text-ink/40';

const LABEL_INPUT_CLASS = `${INPUT_CLASS} text-sm font-semibold text-ink/80 mb-1.5`;
const LABEL_CLASS = 'block text-sm font-semibold text-ink/80 mb-1';

export interface RequisitesAnketaProps {
  rows: ContractRequisite[];
  /** «Заказчик» / «Исполнитель» — для aria-label контекстов. */
  sideLabel: string;
  /** Id prefix для testid scoping (`customer-requisites` / `executor-requisites`). */
  testIdPrefix: string;
  onUpdateValue: (rowId: string, value: string) => void;
  onUpdateLabel: (rowId: string, value: string) => void;
  onRemove: (rowId: string) => void;
  onAdd: () => void;
}

export function RequisitesAnketa({
  rows,
  sideLabel,
  testIdPrefix,
  onUpdateValue,
  onUpdateLabel,
  onRemove,
  onAdd,
}: RequisitesAnketaProps) {
  // Phase 7 hotfix round 6 (R6-#1, 2026-05-26) — ref-debounce 300ms guard.
  // R5-#3 touch-manipulation alone не помог в Aibek visual verify round 11 —
  // add button продолжал диспатчить 2 раза на single tap (iOS Safari preview).
  // Phase A code-level investigation не дал conclusive root cause без iOS
  // device; Phase B fallback: client-side guard guarantees single dispatch
  // независимо от первоисточника double-fire (ghost-click / pointer-emit /
  // reflow-tap-replay). См. ADR-0025 §troubleshooting и handover round 6.
  const lastAddClickRef = useRef<number>(0);
  const handleAddDebounced = useCallback(() => {
    const now = Date.now();
    if (now - lastAddClickRef.current < 300) {
      if (import.meta.env.DEV) {
        console.warn('[RequisitesAnketa] add row click debounced (gap < 300ms)');
      }
      return;
    }
    lastAddClickRef.current = now;
    onAdd();
  }, [onAdd]);

  return (
    <div
      className="flex flex-col gap-2.5"
      data-testid={`${testIdPrefix}-anketa`}
      aria-label={`Реквизиты ${sideLabel}`}
    >
      {rows.map((row) => {
        const fieldId = `${testIdPrefix}-${row.id}`;
        const rowName = row.removable ? row.label.trim() || 'без названия' : row.label;

        if (row.removable) {
          return (
            <div key={row.id} className="flex items-end gap-1.5">
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={row.label}
                  onChange={(e) => onUpdateLabel(row.id, e.target.value)}
                  placeholder="Например: Email"
                  aria-label={`Название поля реквизита ${sideLabel}`}
                  data-testid={`${fieldId}-label-input`}
                  className={LABEL_INPUT_CLASS}
                />
                <input
                  id={fieldId}
                  type="text"
                  value={row.value}
                  onChange={(e) => onUpdateValue(row.id, e.target.value)}
                  placeholder="—"
                  aria-label={`Значение реквизита: ${rowName} ${sideLabel}`}
                  data-testid={`${fieldId}-value-input`}
                  className={INPUT_CLASS}
                />
              </div>
              <button
                type="button"
                onClick={() => onRemove(row.id)}
                aria-label={`Удалить реквизит ${rowName} ${sideLabel}`}
                data-testid={`${fieldId}-remove`}
                className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md text-destructive focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-70"
              >
                <Trash2 size={18} aria-hidden="true" />
              </button>
            </div>
          );
        }

        return (
          <div key={row.id}>
            <label htmlFor={fieldId} className={LABEL_CLASS}>
              {row.label}
            </label>
            <input
              id={fieldId}
              type="text"
              value={row.value}
              onChange={(e) => onUpdateValue(row.id, e.target.value)}
              placeholder="—"
              aria-label={`${row.label} ${sideLabel}`}
              data-testid={`${fieldId}-value-input`}
              className={INPUT_CLASS}
            />
          </div>
        );
      })}

      {/* Phase 7 hotfix round 6 (R6-#1, 2026-05-26) — ref-debounce 300ms guard
          (см. handleAddDebounced выше). R5-#3 touch-manipulation сохранён как
          дополнительная защита (cheap CSS), но root fix теперь client-side. */}
      <button
        type="button"
        onClick={handleAddDebounced}
        aria-label={`Добавить реквизит ${sideLabel}`}
        data-testid={`${testIdPrefix}-add-row`}
        className="w-full min-h-tap-chip mt-1 rounded-xl border-2 border-dashed border-accent bg-card text-ink text-base font-semibold flex items-center justify-center gap-2 touch-manipulation focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
      >
        <Plus size={18} aria-hidden="true" />
        Добавить строку
      </button>
    </div>
  );
}
